import { State } from 'redux/initialState';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';

import actionTypes from 'constants/actionTypes';
import menuTypes from 'constants/menuTypes';

import { useGetActualSociety } from 'hooks/useGetActualSociety';

import { useGetHoldingClassOptions } from 'modules/partners/hooks/useGetHoldingClassesOption';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { store } from 'redux/store';
import { setModal } from 'modules/_shared/redux/modalActions';
import PublishTradingOfferModal from 'modules/trading/modals/PublishTradingOffer';
import { PublishTradingOfferModalProvider } from 'modules/trading/modals/PublishTradingOffer/context/PublishTradingOfferModalContext';
import { TradingOffer, TradingOfferType } from 'types/TradingOffer';
import { AddTradingOfferPageContextType } from '../types/AddTradingOfferPageContextType';
import { addTradingOfferPageInitValue } from '../constants/addTradingOfferPageInitValue';

import { useGetPrices } from '../hooks/useGetPrices';
import { useGetMaxSharesToTransfer } from '../hooks/useGetMaxSharesToTransfer';
import { useGetPartnerCurrentSharesCount } from '../hooks/useGetPartnerCurrentSharesCount';

export const AddTradingOfferPageContext =
  createContext<AddTradingOfferPageContextType>(addTradingOfferPageInitValue);

export const AddTradingOfferPageProvider: FC<{
  children: ReactNode;
  tradingOfferType: TradingOfferType;
  actionType?: string;
}> = ({ children, tradingOfferType, actionType = actionTypes.ADD }) => {
  const { societyId, partnerId, tradingOfferId } = useParams<{
    societyId: string;
    partnerId: string;
    tradingOfferId: string;
  }>();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const [selectedPartner, setSelectedPartner] = useState<string>(
    partnerId || ''
  );
  const [sharesToTransfer, setSharesToTransfer] = useState<number>(0);
  const [isPartialAmountEnabled, setIsPartialAmountEnabled] =
    useState<boolean>(false);
  const [price, setPrice] = useState<number>(0);
  const [isPricePerShare, setIsPricePerShare] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: pricesData } = useGetPrices({
    price,
    sharesToTransfer,
    isPricePerShare,
  });

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptions({
      societyId,
      partnerId,
    });

  const { data: partnerCurrentSharesCountData } =
    useGetPartnerCurrentSharesCount({
      selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
    });

  const {
    data: holdingClassOptionsData,
    actions: holdingClassOptionsActions,
    isLoading: isLoadingHoldingClassOptions,
  } = useGetHoldingClassOptions({
    societyId,
    // holdingClassId: draftInvestorToEdit?.holdingClass,
    partnerId: selectedPartner,
  });

  const { data: maxSharesToTransferData } = useGetMaxSharesToTransfer({
    partnerActualSharesCount:
      partnerCurrentSharesCountData.partnerCurrentSharesCount,
    selectedHoldingClassOption:
      holdingClassOptionsData.selectedHoldingClassOption,
  });

  const handleChangeSharesToTransfer = (value: number) => {
    setSharesToTransfer(value);
  };

  const handleChangeIsPartialAmountEnabled = () => {
    setIsPartialAmountEnabled((prev) => !prev);
  };

  const handleChangePrice = (value: number) => {
    setPrice(value);
  };

  const handleChangeIsPricePerShare = () => {
    setIsPricePerShare((prev) => !prev);
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleChangeEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleChangePartner = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedPartner(selectedOption ? selectedOption.value : '');
    partnerOptionsActions.handleChangePartnerOption(selectedOption);
    setSharesToTransfer(0);
  };

  const handleChangeHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    holdingClassOptionsActions.handleChangeSelectedHoldingClass(selectedOption);
    setSharesToTransfer(0);
  };

  const handleReviewTradingOffer = () => {
    if (!societyId || !selectedPartner) return;

    const tradingOffer: Partial<TradingOffer> & { className: string } = {
      society: societyId,
      partner: selectedPartner,
      shareClass:
        (holdingClassOptionsData.selectedHoldingClassOption?.value as string) ||
        null,
      className:
        holdingClassOptionsData?.selectedHoldingClassOption?.name || '-',
      sharesCount: {
        total: sharesToTransfer,
        agreed: 0,
        approved: 0,
      },
      sharePrice: pricesData.pricePerShare,
      startDate,
      endDate,
      type: tradingOfferType,
      isPartialAmountEnabled,
      documents: [],
    };

    store.dispatch(
      setModal(
        <PublishTradingOfferModalProvider
          tradingOffer={tradingOffer}
          modalActionType={actionType}
        >
          <PublishTradingOfferModal />
        </PublishTradingOfferModalProvider>
      )
    );
  };

  return (
    <AddTradingOfferPageContext.Provider
      value={{
        data: {
          tradingOfferType,
          actionType,
          partnerOptions: partnerOptionsData.partnerOptions || [],
          selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
          holdingClassOptions:
            holdingClassOptionsData.holdingClassOptions || [],
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          partnerCurrentSharesCount:
            partnerCurrentSharesCountData.partnerCurrentSharesCount,
          sharesToTransfer,
          maxSharesToTransfer: maxSharesToTransferData.maxSharesToTransfer,
          isPartialAmountEnabled,
          price,
          pricePerShare: pricesData.pricePerShare,
          totalOfferPrice: pricesData.totalOfferPrice,
          societyId: societyId || '',
          societyName: actualSociety?.name || '',
          isPricePerShare,
          startDate,
          endDate,
          isPartnerView: !!partnerId,
          isAdmin: true,
          isReadOnly: false,
          isLoading: isLoading || !actualSociety,
        },
        actions: {
          handleChangePartner,
          handleSearchPartnerByTerm:
            partnerOptionsActions.handleSearchPartnerByTerm,
          handleChangeHoldingClass,
          handleSearchHoldingClassByTerm:
            holdingClassOptionsActions.handleSearchHoldingClassByTerm,
          handleChangeSharesToTransfer,
          handleChangeIsPartialAmountEnabled,
          handleChangePrice,
          handleChangeIsPricePerShare,
          handleChangeStartDate,
          handleChangeEndDate,
          handleReviewTradingOffer,
        },
      }}
    >
      {children}
    </AddTradingOfferPageContext.Provider>
  );
};

export const useAddTradingOfferPage = () => {
  const context = useContext(AddTradingOfferPageContext);
  if (!context) {
    throw new Error(
      'useAddTradingOfferPage must be used within a AddTradingOfferPageProvider'
    );
  }
  return context;
};
